/**
 * Notification Context
 *
 * Gestiona el estado de notificaciones y la conexión WebSocket
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { io } from "socket.io-client";
import { useAuth } from "./AuthContext";
import * as notificationsService from "../services/notificationsService";
import { requestNotificationPermission as requestFCMToken, onMessageListener } from "../config/firebaseConfig";

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotifications must be used within NotificationProvider");
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [socket, setSocket] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [connected, setConnected] = useState(false);
  const [toastNotification, setToastNotification] = useState(null);
  const [nativeNotificationsEnabled, setNativeNotificationsEnabled] = useState(false);
  const [fcmToken, setFcmToken] = useState(null);
  const previousNotificationsRef = React.useRef([]);

  // ============================================================
  // FCM INITIALIZATION - TEMPORALMENTE DESHABILITADO
  // Para habilitar: descomenta el bloque useEffect de abajo
  // ============================================================
  /*
  useEffect(() => {
    const initializeFCM = async () => {
      console.log("🔄 Intentando inicializar FCM...", {
        hasUser: !!user,
        hasToken: !!token,
        userId: user?.id,
        authProvider: user?.authProvider
      });

      if (!user || !token) {
        console.log("⏭️ Saltando inicialización FCM - no hay user o token");
        return;
      }

      try {
        // Solicitar token FCM
        console.log("📲 Solicitando token FCM...");
        const fcmTokenResult = await requestFCMToken();

        if (fcmTokenResult) {
          console.log("📱 FCM Token obtenido:", fcmTokenResult.substring(0, 20) + "...");
          setFcmToken(fcmTokenResult);
          setNativeNotificationsEnabled(true);

          // Guardar token en localStorage para poder eliminarlo en el logout
          localStorage.setItem("fcmToken", fcmTokenResult);

          // Enviar token al backend para guardarlo
          console.log("📤 Enviando token FCM al backend...");
          try {
            await notificationsService.saveFCMToken(fcmTokenResult);
            console.log("✅ Token FCM registrado en el servidor para userId:", user.id);
          } catch (saveError) {
            console.error("❌ Error al guardar token FCM en el servidor:", saveError);
            // Mostrar detalles del error
            if (saveError.response) {
              console.error("   Respuesta del servidor:", saveError.response.data);
              console.error("   Status:", saveError.response.status);
            }
          }
        } else {
          console.warn("⚠️ No se pudo obtener token FCM (permisos denegados?)");
        }
      } catch (error) {
        console.error("❌ Error inicializando FCM:", error);
      }
    };

    initializeFCM();

    // No hacemos cleanup aquí porque causa problemas de timing
    // El cleanup se hará en el logout
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token]);
  */
  // ============================================================

  // Función para mostrar notificación nativa del browser
  const showNativeNotification = useCallback((notification) => {
    // TEMPORALMENTE DESHABILITADO - solo FCM en background debería mostrar notificaciones
    console.log('⏭️ showNativeNotification deshabilitado - usando solo FCM');
    return;

    /* CÓDIGO ORIGINAL COMENTADO
    if (!nativeNotificationsEnabled || !("Notification" in window)) {
      return;
    }

    if (Notification.permission === "granted") {
      // Usar el logo de la empresa para las notificaciones
      // Usando logo192.png que ya existe en la carpeta public
      const notificationIcon = "/logo192.png";
      const notificationBadge = "/logo192.png"; // Badge para Android

      try {
        const nativeNotif = new Notification(notification.title, {
          body: notification.message,
          icon: notificationIcon,
          badge: notificationBadge,
          tag: `notification-${notification.id}`,
          requireInteraction: false,
          silent: false,
          // Agregar imagen grande opcional (se ve en algunas plataformas)
          image: notificationIcon
        });

        // Manejar click en la notificación
        nativeNotif.onclick = () => {
          window.focus();
          nativeNotif.close();

          // Si tiene entityType y entityId, navegar a esa entidad
          if (notification.entityType && notification.entityId) {
            const routes = {
              "solicitudes": `/solicitudes/${notification.entityId}`,
              "directores": `/directores`,
              "liquidaciones": `/intermediarios/liquidaciones`
            };

            const route = routes[notification.entityType];
            if (route) {
              window.location.href = route;
            }
          }
        };

        // Auto-cerrar después de 10 segundos
        setTimeout(() => {
          nativeNotif.close();
        }, 10000);

        console.log("🔔 Native notification shown:", notification.title);
      } catch (error) {
        console.error("Error showing native notification:", error);
      }
    }
    FIN CÓDIGO ORIGINAL COMENTADO */
  }, [nativeNotificationsEnabled]);

  // Inicializar conexión Socket.IO
  useEffect(() => {
    if (!user || !token) {
      // Cerrar socket si existe y no hay usuario autenticado
      if (socket) {
        socket.disconnect();
        setSocket(null);
        setConnected(false);
      }
      return;
    }

    // Crear conexión Socket.IO
    const newSocket = io(process.env.REACT_APP_API_URL.replace('/api', ''), {
      auth: {
        token: token
      },
      transports: ['websocket', 'polling']
    });

    newSocket.on('connect', () => {
      console.log('🔌 Socket.IO connected');
      setConnected(true);
    });

    newSocket.on('disconnect', () => {
      console.log('🔌 Socket.IO disconnected');
      setConnected(false);
    });

    newSocket.on('notification', (notification) => {
      console.log('🔔 New notification received via Socket.IO:', notification);

      // Agregar a la lista de notificaciones
      setNotifications(prev => [notification, ...prev]);

      // Incrementar contador si no está leída
      if (!notification.isRead) {
        setUnreadCount(prev => prev + 1);
      }

      // Mostrar toast
      setToastNotification(notification);

      // Mostrar notificación nativa del browser
      showNativeNotification(notification);
    });

    newSocket.on('connect_error', (error) => {
      console.error('Socket.IO connection error:', error);
      setConnected(false);
    });

    setSocket(newSocket);

    // Cleanup al desmontar
    return () => {
      newSocket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token, showNativeNotification]);

  // ============================================================
  // FCM FOREGROUND LISTENER - TEMPORALMENTE DESHABILITADO
  // Para habilitar: descomenta el bloque useEffect de abajo
  // ============================================================
  /*
  useEffect(() => {
    if (!user) return;

    // Listener para notificaciones FCM en foreground
    onMessageListener((payload) => {
      console.log('📬 Mensaje FCM recibido (foreground):', payload);

      // Si Socket.IO está conectado, ignorar FCM (ya lo maneja Socket.IO)
      if (connected) {
        console.log('⏭️ Ignorando FCM - Socket.IO está conectado');
        return;
      }

      // Crear objeto de notificación compatible con el formato actual
      const notification = {
        id: payload.data?.notificationId || Date.now(),
        title: payload.notification?.title || 'Nueva notificación',
        message: payload.notification?.body || '',
        entityType: payload.data?.entityType,
        entityId: payload.data?.entityId,
        isRead: false,
        createdAt: new Date()
      };

      // Agregar a la lista de notificaciones
      setNotifications(prev => [notification, ...prev]);

      // Incrementar contador
      setUnreadCount(prev => prev + 1);

      // Mostrar toast
      setToastNotification(notification);

      // Mostrar notificación nativa del browser
      showNativeNotification(notification);
    });
  }, [user, connected, showNativeNotification]);
  */
  // ============================================================

  // Cargar notificaciones iniciales
  useEffect(() => {
    if (user && token) {
      loadNotifications();
      loadUnreadCount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token]);

  // Polling automático para cargar nuevas notificaciones cada 30 segundos
  useEffect(() => {
    if (!user || !token) return;

    const pollingInterval = setInterval(() => {
      console.log("🔄 Polling notifications...");
      loadNotifications();
      loadUnreadCount();
    }, 30000); // 30 segundos

    return () => {
      clearInterval(pollingInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token]);

  // Cargar notificaciones desde el servidor
  const loadNotifications = useCallback(async (onlyUnread = false) => {
    try {
      const response = await notificationsService.getMyNotifications(onlyUnread, 50);
      const newNotifications = response.data || [];

      const previousNotifications = previousNotificationsRef.current;

      console.log("📥 Loaded notifications:", {
        count: newNotifications.length,
        previousCount: previousNotifications.length,
        firstId: newNotifications[0]?.id
      });

      // Detectar notificaciones nuevas (que no estaban en el estado anterior)
      if (previousNotifications.length > 0 && newNotifications.length > 0) {
        const existingIds = previousNotifications.map(n => n.id);
        const reallyNewNotifications = newNotifications.filter(n => !existingIds.includes(n.id));

        console.log("🔍 Checking for new notifications:", {
          existingIds,
          newIds: newNotifications.map(n => n.id),
          reallyNewCount: reallyNewNotifications.length
        });

        // Mostrar toast solo para la notificación más reciente
        if (reallyNewNotifications.length > 0) {
          const newest = reallyNewNotifications[0];
          console.log("🆕 New notification detected via polling:", newest);
          setToastNotification(newest);

          // Mostrar notificación nativa del browser
          showNativeNotification(newest);
        }
      }

      // Actualizar ref para la próxima comparación
      previousNotificationsRef.current = newNotifications;
      setNotifications(newNotifications);
    } catch (error) {
      console.error("Error loading notifications:", error);
    }
  }, [showNativeNotification]);

  // Cargar contador de no leídas
  const loadUnreadCount = async () => {
    try {
      const count = await notificationsService.getUnreadCount();
      setUnreadCount(count);
    } catch (error) {
      console.error("Error loading unread count:", error);
    }
  };

  // Marcar notificación como leída
  const markAsRead = useCallback(async (notificationId) => {
    try {
      await notificationsService.markAsRead(notificationId);

      // Actualizar estado local
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notificationId ? { ...n, isRead: true, readAt: new Date() } : n
        )
      );

      // Decrementar contador
      setUnreadCount((prev) => Math.max(0, prev - 1));

      // Emitir evento a Socket.IO (opcional)
      if (socket) {
        socket.emit("mark-notification-read", notificationId);
      }
    } catch (error) {
      console.error("Error marking notification as read:", error);
      throw error;
    }
  }, [socket]);

  // Marcar todas como leídas
  const markAllAsRead = useCallback(async () => {
    try {
      await notificationsService.markAllAsRead();

      // Actualizar estado local
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, isRead: true, readAt: new Date() }))
      );

      // Resetear contador
      setUnreadCount(0);
    } catch (error) {
      console.error("Error marking all as read:", error);
      throw error;
    }
  }, []);

  // Eliminar notificación
  const deleteNotification = useCallback(async (notificationId) => {
    try {
      await notificationsService.deleteNotification(notificationId);

      // Actualizar estado local
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));

      // Decrementar contador si era no leída
      const notification = notifications.find((n) => n.id === notificationId);
      if (notification && !notification.isRead) {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error("Error deleting notification:", error);
      throw error;
    }
  }, [notifications]);

  // Cerrar toast
  const closeToast = useCallback(() => {
    setToastNotification(null);
  }, []);

  // Solicitar permisos de notificaciones manualmente
  const requestNotificationPermission = useCallback(async () => {
    if (!("Notification" in window)) {
      console.warn("Browser does not support notifications");
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      console.log("🔔 Notification permission requested, result:", permission);

      if (permission === "granted") {
        setNativeNotificationsEnabled(true);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error requesting notification permission:", error);
      return false;
    }
  }, []);

  const value = {
    notifications,
    unreadCount,
    connected,
    toastNotification,
    nativeNotificationsEnabled,
    loadNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    closeToast,
    requestNotificationPermission
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
